/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { Fragment, Suspense } from 'react';

import { css } from '@compiled/react';
import { defineMessages, type MessageDescriptor, useIntl } from 'react-intl';

import IconButton from '@atlaskit/button/icon/button';
import { cssMap, cx, jsx } from '@atlaskit/css';
import CloseIcon from '@atlaskit/icon/core/cross';
import ExitingPersistence from '@atlaskit/motion/exiting-persistence';
import SlideIn from '@atlaskit/motion/slide-in';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { useUIConfig } from '../../common/utils/ui-config/useUIConfig';
import { useSplitView } from '../split-layout/useSplitView';
import { SessionRightPanelErrorBoundary } from './SessionRightPanelErrorBoundary';
import { SessionRightPanelLazy } from './SessionRightPanelLazy';
import { SessionRightPanelSkeleton } from './SessionRightPanelSkeleton';

type MessageKeys = 'closeContextPanelLabel' | 'contextPanelLabel';

const messages: Record<MessageKeys, MessageDescriptor> = defineMessages({
	contextPanelLabel: {
		id: 'ai-mate.conversation-assistant.session-context-panel-drawer.label-non-final',
		defaultMessage: 'Context panel',
		description:
			'Accessible name for the context panel dialog that shows sources and output from the conversation.',
	},
	closeContextPanelLabel: {
		id: 'ai-mate.conversation-assistant.session-context-panel-drawer.close-label-non-final',
		defaultMessage: 'Hide context',
		description:
			'Label for the button that closes the context panel drawer and returns the user to the chat.',
	},
});

const styles = cssMap({
	// Base shell primitive mirrored from rovo-navigation's side-nav: absolute overlay layer that both
	// backdrop and tray reuse.
	sideNavContent: {
		position: 'absolute',
		top: token('space.0'),
		right: token('space.0'),
		bottom: token('space.0'),
		zIndex: 1,
		overflowX: 'hidden',
	},
	// Backdrop should cover the full host surface (chat + tray), matching hamburger behavior.
	backdropCover: {
		left: token('space.0'),
		borderRadius: 'inherit',
	},
	// Right-anchor variant: keep tray width constrained and pinned to the right side.
	rightTray: {
		width: '100%',
	},
	trayNonFullscreen: {
		maxWidth: '90%',
	},
	trayFullscreen: {
		maxWidth: '50%',
	},
	// Opaque sliding panel. The surface background lives here (not on the tray) so the whole panel
	// animates in as a single unit, exactly like the hamburger side-nav. Uses the same `elevation.surface`
	// token as the hamburger; separation from the chat comes from the scrim backdrop (no shadow).
	panel: {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		backgroundColor: token('elevation.surface'),
	},
	// Applied on top of `panel` inside the chat mini-modal, matching the hamburger side-nav's
	// `rootMiniModal`: a raised surface with the xxlarge modal corner radius.
	panelMiniModal: {
		backgroundColor: token('elevation.surface.raised'),
		borderRadius: token('radius.xxlarge'),
	},
	// Right-aligned so the close button mirrors the hamburger's top-of-menu placement on the opposite
	// (leading) edge for a right-anchored panel. Same `space.150` padding as the hamburger content.
	toolbar: {
		display: 'flex',
		flexShrink: 0,
		alignItems: 'center',
		justifyContent: 'flex-end',
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
	},
	body: {
		flexGrow: 1,
		minHeight: '0px',
	},
});

// The scrim is authored with `@compiled/react` because `@atlaskit/css`'s typed `cssMap` does not
// expose this token as an allowed `backgroundColor` value. Uses the same token as the hamburger
// side-nav backdrop so the two menus dim the surface identically.
const backdropStyles = css({
	backgroundColor: token('elevation.shadow.overflow.perimeter'),
	cursor: 'pointer',
	border: 0,
	padding: 0,
});

type SessionContextPanelDrawerProps = {
	/**
	 * Whether the drawer is open. Kept mounted while closed (rendering nothing) so the slide-out
	 * exit animation can play on close, mirroring the hamburger side-nav.
	 */
	isOpen: boolean;
	/** Applies the chat mini-modal surface treatment (raised surface + xxlarge radius). */
	isMiniModal?: boolean;
};

/**
 * Session context overlay used when there is not room for a side-by-side split (narrow surfaces, the
 * mini-modal, or when reopening after a split auto-collapses). `SessionContextLayout` keeps it mounted
 * and toggles `isOpen` so the enter/exit animations can play.
 *
 * It reuses the hamburger side-nav's menu shell — an `ExitingPersistence` wrapper, a clickable scrim
 * backdrop, and a max-width tray whose opaque panel slides in via `SlideIn` — but is anchored to and
 * slides in/out from the right. A circular close button sits at the top of the panel, matching the
 * hamburger menu.
 */
export const SessionContextPanelDrawer = ({
	isOpen,
	isMiniModal = false,
}: SessionContextPanelDrawerProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const { closeSessionContext } = useSplitView();
	const { fullScreen } = useUIConfig();

	return (
		<ExitingPersistence>
			{isOpen && (
				<Fragment>
					<button
						type="button"
						aria-label={formatMessage(messages.closeContextPanelLabel)}
						css={[styles.sideNavContent, styles.backdropCover, backdropStyles]}
						onClick={closeSessionContext}
					/>
					<Box
						xcss={cx(
							styles.sideNavContent,
							styles.rightTray,
							fullScreen ? styles.trayFullscreen : styles.trayNonFullscreen,
						)}
						testId="conversation-assistant-session-context-panel-drawer"
						role="dialog"
						aria-label={formatMessage(messages.contextPanelLabel)}
					>
						<SlideIn enterFrom="right" exitTo="right" fade="inout" duration="medium">
							{(animationProps) => (
								<div
									{...animationProps}
									// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- forwarding SlideIn's transform requires its inline style; height fills the tray so the whole opaque panel slides as one unit (mirrors the hamburger side-nav)
									style={{ ...animationProps.style, height: '100%' }}
								>
									<div css={[styles.panel, isMiniModal && styles.panelMiniModal]}>
										<Box xcss={styles.toolbar}>
											<IconButton
												testId="conversation-assistant-session-context-panel-drawer-close-button"
												shape="circle"
												appearance="subtle"
												isTooltipDisabled={false}
												label={formatMessage(messages.closeContextPanelLabel)}
												icon={CloseIcon}
												onClick={closeSessionContext}
											/>
										</Box>
										<Box xcss={styles.body}>
											<SessionRightPanelErrorBoundary>
												<Suspense fallback={<SessionRightPanelSkeleton />}>
													<SessionRightPanelLazy />
												</Suspense>
											</SessionRightPanelErrorBoundary>
										</Box>
									</div>
								</div>
							)}
						</SlideIn>
					</Box>
				</Fragment>
			)}
		</ExitingPersistence>
	);
};
